import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    GetApprovalTrackerProgress,
    UpdateApprovalTrackerFlow
} from "../../../../reduxToolkit/ApprovalTracker/ApprovalTrackerApi";
import {errorMsg} from "../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../enums/statusEnum";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * ApprovalTrackerApp Hook
 *
 * Custom React hook that handles approval tracking logic, including loading tracker data,
 * controlling modal states (approve, deny, request changes), and managing related user interaction state.
 *
 * This hook is typically used in pages that need to display or manage approval flow progress for a specific module.
 *
 * */


export const ApprovalTrackerApp = ()=>{
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {module,subjectId,approvalFlowId} = useParams()
    const dispatch = useDispatch(); // Redux dispatch function
    const location = useLocation();
    let { state } = location;
    const backUrl = state?.backUrl;
    const isOverdue = backUrl?.state?.isOverdue;
    const isCanceled = backUrl?.state?.isCanceled;
    const { getApprovalTrackerProgress } = useSelector((state) => state.ApprovalTrackerSlice);
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [showIssueComment, setShowIssueComment] = useState(false);
    const [modified,setModified] = useState(false)
    const [row, setRow] = useState(null);
    const [showMsg, setShowMsg] = useState(false);

    const genericCommentModals =[
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.APPROVED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.DENIED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'request_changes',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.CHANGES_REQUESTED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
    ];


    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    // useEffect that runs every time `modified` value changes
    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        dispatch(GetApprovalTrackerProgress({
            approvalFlowId: approvalFlowId,
            subjectId: subjectId,
            module: module,
        })).then(
            ({ payload: { data: {success, code,data } } }) => {
                if (success === true) {
                    const isUserApprover = data?.levels?.some(level =>
                        level.approvers?.some(approver => approver.userId === user?.data?.id)
                    ) || false;
                    setShowMsg(isUserApprover);
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    const handleOnBack = () => {
        navigate(
            backUrl.pathname, {
            state: backUrl.state
        });
    }

    const currentStatus = isCanceled
    ? STATUS_MODEL.CANCELED
    : getApprovalTrackerProgress?.overallStatus?.id;

    const notSettled = getApprovalTrackerProgress?.overallStatus?.id !== STATUS_MODEL.APPROVED &&
        getApprovalTrackerProgress?.overallStatus?.id !== STATUS_MODEL.DENIED;

    const bannerProps = isCanceled ?
        {
            className: 'chip-error-code-150-100',
            name: t('invitation_has_been_canceled_it_is_not_possible_complete_approval'),
            classNameIcon: 'icon-color-error-code-700'
        }
        : isOverdue && notSettled ?
            {
                className: 'chip-error-code-150-100',
                name: t('invitation_has_expired_it_is_not_possible_complete_approval'),
                classNameIcon: 'icon-color-error-code-700'
            }
            : showMsg && notSettled ?
                {
                    className: 'chip-secondary-150-100',
                    name: t('your_approve_is_required'),
                    classNameIcon: 'icon-color-secondary-700'
                }
                : null;

    return {
        t,
        genericCommentModals,
        setShowApprove,
        setShowDenied,
        setShowIssueComment,
        setRow,
        setModified,
        modified,
        getApprovalTrackerProgress,
        user,
        module,
        isOverdue,
        handleOnBack,
        isCanceled,
        currentStatus,
        bannerProps
    }
}
